import { useEffect } from 'react';
import { useLocation } from 'react-router-dom';
import { fetchPublicSettings } from '../../features/settings/api/settings';

function upsertMeta(attr: 'name' | 'property', key: string, content: string) {
  if (!content) return;
  let el = document.head.querySelector(`meta[${attr}="${key}"]`) as HTMLMetaElement | null;
  if (!el) {
    el = document.createElement('meta');
    el.setAttribute(attr, key);
    document.head.appendChild(el);
  }
  el.setAttribute('content', content);
}

function upsertCanonical(href: string) {
  if (!href) return;
  let el = document.head.querySelector('link[rel="canonical"]') as HTMLLinkElement | null;
  if (!el) {
    el = document.createElement('link');
    el.setAttribute('rel', 'canonical');
    document.head.appendChild(el);
  }
  el.setAttribute('href', href);
}

function injectOnce(id: string, code: string) {
  if (!code || document.getElementById(id)) return;
  const s = document.createElement('script');
  s.id = id;
  s.innerHTML = code;
  document.head.appendChild(s);
}

// Injects SEO meta + tracking pixels from admin Settings into the public site head.
// Also fires PageView on every SPA route change (React Router).
export function SeoHead() {
  const location = useLocation();

  useEffect(() => {
    fetchPublicSettings()
      .then((s: any) => {
        if (s.default_meta_title || s.site_name) document.title = s.default_meta_title || s.site_name;
        upsertMeta('name', 'description', s.default_meta_description || '');
        upsertMeta('name', 'keywords', s.default_meta_keywords || '');
        upsertMeta('property', 'og:title', s.default_meta_title || '');
        upsertMeta('property', 'og:description', s.default_meta_description || '');
        upsertMeta('property', 'og:image', s.default_og_image_url || '');
        upsertMeta('property', 'og:type', 'website');
        upsertCanonical(s.canonical_base_url || '');
        if (s.favicon_url) {
          let fav = document.head.querySelector('link[rel="icon"]') as HTMLLinkElement | null;
          if (!fav) { fav = document.createElement('link'); fav.rel = 'icon'; document.head.appendChild(fav); }
          fav.href = s.favicon_url;
        }
        const validMeta = (v: string) => /^\d{6,20}$/.test(v || '');
        const validTikTok = (v: string) => /^[A-Za-z0-9]{8,40}$/.test(v || '') && !/placeholder|example|xxxx|test/i.test(v || '');
        const validGtm = (v: string) => /^GTM-[A-Z0-9]{4,}$/i.test(v || '');
        if (validMeta(s.meta_pixel_id)) {
          injectOnce('meta-pixel', `!function(f,b,e,v,n,t,s){if(f.fbq)return;n=f.fbq=function(){n.callMethod?n.callMethod.apply(n,arguments):n.queue.push(arguments)};if(!f._fbq)f._fbq=n;n.push=n;n.loaded=!0;n.version='2.0';n.queue=[];t=b.createElement(e);t.async=!0;t.src=v;s=b.getElementsByTagName(e)[0];s.parentNode.insertBefore(t,s)}(window,document,'script','https://connect.facebook.net/en_US/fbevents.js');fbq('init','${s.meta_pixel_id}');fbq('track','PageView');`);
        }
        if (validTikTok(s.tiktok_pixel_id)) {
          injectOnce('tiktok-pixel', `!function(w,d,t){w.TiktokAnalyticsObject=t;var ttq=w[t]=w[t]||[];ttq.methods=["page","track","identify","instances","debug","on","off","once","ready","alias","group","enableCookie","disableCookie"];ttq.setAndDefer=function(t,e){t[e]=function(){t.push([e].concat(Array.prototype.slice.call(arguments,0)))}};for(var i=0;i<ttq.methods.length;i++)ttq.setAndDefer(ttq,ttq.methods[i]);ttq.instance=function(t){for(var e=ttq._i[t]||[],n=0;n<ttq.methods.length;n++)ttq.setAndDefer(e,ttq.methods[n]);return e};ttq.load=function(e,n){var i="https://analytics.tiktok.com/i18n/pixel/events.js";ttq._i=ttq._i||{};ttq._i[e]=[];ttq._i[e]._u=i;ttq._t=ttq._t||{};ttq._t[e]=+new Date;ttq._o=ttq._o||{};ttq._o[e]=n||{};var o=d.createElement("script");o.type="text/javascript";o.async=!0;o.src=i+"?sdkid="+e+"&lib="+t;var a=d.getElementsByTagName("script")[0];a.parentNode.insertBefore(o,a)};ttq.load('${s.tiktok_pixel_id}');ttq.page()}(window,document,'ttq');`);
        }
        if (validGtm(s.gtm_id)) {
          injectOnce('gtm', `(function(w,d,s,l,i){w[l]=w[l]||[];w[l].push({'gtm.start':new Date().getTime(),event:'gtm.js'});var f=d.getElementsByTagName(s)[0],j=d.createElement(s),dl=l!='dataLayer'?'&l='+l:'';j.async=true;j.src='https://www.googletagmanager.com/gtm.js?id='+i+dl;f.parentNode.insertBefore(j,f)})(window,document,'script','dataLayer','${s.gtm_id}');`);
        }
      })
      .catch(() => {});
  }, []);

  // Fire PageView on every SPA route change (after initial pixel init above).
  useEffect(() => {
    try {
      const w: any = window;
      if (typeof w.fbq === 'function') w.fbq('track', 'PageView');
      if (w.ttq && typeof w.ttq.page === 'function') w.ttq.page();
      if (Array.isArray(w.dataLayer)) w.dataLayer.push({ event: 'page_view', page_path: location.pathname + location.search });
    } catch {
      /* pixel errors must never break the app */
    }
  }, [location.pathname, location.search]);

  return null;
}
